import {Ticker} from '../features/ticker';
import {Slider} from '../features/slider';
import {isDesktop} from '../util/media-query';

const selectors = {
  barSlide: '[data-slide]',
  frame: '[data-ticker-frame]',
  header: '[data-header-wrapper]',
  slider: '[data-slider]',
  marquee: '[data-marquee]',
  tickerScale: '[data-ticker-scale]',
  tickerText: '[data-ticker-text]',
};

const classes = {
  tickerAnimated: 'ticker--animated',
  mobile: 'mobile',
  desktop: 'desktop',
};

const attributes = {
  slide: 'data-slide',
  stop: 'data-stop',
  style: 'style',
  targetReferrer: 'data-target-referrer',
};

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.locationPath = location.href;

    this.slides = this.querySelectorAll(selectors.barSlide);
    this.frames = this.querySelectorAll(selectors.frame);
    this.slider = this.querySelector(selectors.slider);
    this.marquee = this.querySelector(selectors.marquee);
    this.enableSlider = isDesktop();
    this.initSliderEvent = (event) => this.initSlider(event);
    this.tickerResizeEvent = (event) => this.onTickerResize(event);
  }

  connectedCallback() {
    this.removeAnnouncement();

    if (this.slider) {
      this.initSliders();
    }

    if (this.marquee) {
      this.initTickers(true);
    }

    this.addEventListener('theme:block:select', (e) => {
      this.onBlockSelect(e);
    });

    this.addEventListener('theme:block:deselect', (e) => {
      this.onBlockDeselect(e);
    });

    document.dispatchEvent(new CustomEvent('theme:announcement:init', {bubbles: true}));
  }

  /**
   * Delete announcement which has a target referrer attribute and it is not contained in page URL
   */
  removeAnnouncement() {
    for (let index = 0; index < this.slides.length; index++) {
      const element = this.slides[index];

      if (!element.hasAttribute(attributes.targetReferrer)) {
        continue;
      }

      if (this.locationPath.indexOf(element.getAttribute(attributes.targetReferrer)) === -1 && !window.Shopify.designMode) {
        element.parentNode.removeChild(element);
      }
    }
  }

  /**
   * Init slider
   */
  initSliders() {
    this.initSlider();
    document.addEventListener('theme:resize:width', this.initSliderEvent);

    this.addEventListener('theme:slider:loaded', () => {
      this.initTickers();
    });
  }

  initSlider() {
    const isDesktopView = isDesktop();
    const isMobileView = !isDesktopView;

    if ((isDesktopView && this.enableSlider) || (isMobileView && !this.enableSlider)) {
      this.slider.flkty?.destroy();

      if (isDesktopView && this.enableSlider) {
        this.enableSlider = false;
      } else if (isMobileView && !this.enableSlider) {
        this.enableSlider = true;
      }

      this.slider = new Slider(this, this.querySelector(selectors.slider));
      this.slider.flkty?.reposition();
    }

    // Init tickers if there's only one slide
    if (!this.slider.flkty) {
      this.initTickers();
    }
  }

  /**
   * Init tickers in sliders
   */
  initTickers(stopClone = false) {
    this.frames.forEach((element) => {
      new Ticker(element, stopClone);
    });

    document.addEventListener('theme:resize:width', this.tickerResizeEvent);
  }

  onTickerResize() {
    this.frames.forEach((element) => {
      element.dispatchEvent(new CustomEvent('theme:marquee:refresh'));
    });
  }

  toggleTicker(e, isStopped) {
    const tickerScale = this.querySelector(selectors.tickerScale);
    const element = this.querySelector(`[${attributes.slide}="${e.detail.blockId}"]`);

    if (isStopped && element) {
      tickerScale.setAttribute(attributes.stop, '');
      tickerScale.querySelectorAll(selectors.tickerText).forEach((textHolder) => {
        textHolder.classList.remove(classes.tickerAnimated);
        textHolder.style.transform = `translate3d(${-(element.offsetLeft - element.clientWidth)}px, 0, 0)`;
      });
    }

    if (!isStopped && element) {
      tickerScale.querySelectorAll(selectors.tickerText).forEach((textHolder) => {
        textHolder.classList.add(classes.tickerAnimated);
        textHolder.removeAttribute(attributes.style);
      });
      tickerScale.removeAttribute(attributes.stop);
    }
  }

  onBlockSelect(e) {
    if (this.slider) {
      this.slider.onBlockSelect(e);
    } else {
      this.toggleTicker(e, true);
    }
  }

  onBlockDeselect(e) {
    if (this.slider) {
      this.slider.onBlockDeselect(e);
    } else {
      this.toggleTicker(e, false);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('theme:resize:width', this.initSliderEvent);
    document.removeEventListener('theme:resize:width', this.tickerResizeEvent);
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
